import { useState } from "react";

import { Check, Plus, Type } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import { type ReportPanel } from "../api";

/**
 * Add — or, in `editing` mode, revise — a text/title panel: a heading or note
 * block that isn't backed by a query (rendered by PanelView from `viz.content`).
 * As a creator it's collapsed to a button until opened; as an editor it starts
 * open, pre-filled, and saves in place. Query panels edit via {@link PanelEditor};
 * this is the editor for the query-less kind.
 */
export function TextPanelForm({
  onAdd,
  saving,
  editing = false,
  initialTitle = "",
  initialContent = "",
  onCancel,
}: {
  onAdd: (panel: ReportPanel) => void;
  saving?: boolean;
  /** Edit an existing text panel: start open, pre-filled, "Save changes". */
  editing?: boolean;
  initialTitle?: string;
  initialContent?: string;
  /** Called when the editor is dismissed (edit mode). */
  onCancel?: () => void;
}) {
  const [open, setOpen] = useState(editing);
  const [title, setTitle] = useState(initialTitle);
  const [content, setContent] = useState(initialContent);

  const submit = () => {
    // In edit mode the parent (updatePanel) reuses the existing panel's id and
    // layout, so a fresh id here is harmless.
    onAdd({
      id: crypto.randomUUID(),
      type: "text",
      title: title.trim() || null,
      layout: { x: 0, y: 0, w: 6, h: 4 },
      query: null,
      viz: { content },
    });
    if (!editing) {
      setTitle("");
      setContent("");
      setOpen(false);
    }
  };
  const dismiss = () => (editing ? onCancel?.() : setOpen(false));

  if (!open) {
    return (
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <Type className="h-3.5 w-3.5" />
        Add text panel
      </Button>
    );
  }

  return (
    <div className="space-y-2 rounded-lg border border-border bg-card p-3">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
        <Type className="h-3.5 w-3.5" />
        {editing ? "Edit text panel" : "New text panel"}
      </div>
      <Input
        placeholder="Title (optional)"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="h-8"
      />
      <Textarea
        placeholder="Notes or a heading — plain text / markdown…"
        value={content}
        onChange={(e) => setContent(e.target.value)}
        rows={3}
        className="resize-none"
      />
      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={submit}
          disabled={saving || (!content.trim() && !title.trim())}
        >
          {editing ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
          {editing ? "Save changes" : "Add to report"}
        </Button>
        <Button variant="ghost" size="sm" onClick={dismiss}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
